import type { CatalogItemDto } from '@glory/contracts';
import { formatHours, formatMoney, formatPercent, t } from '@glory/i18n';
import {
  Badge,
  Button,
  Callout,
  cn,
  dataColumns,
  DataTable,
  Dialog,
  EmptyState,
  HybridPanel,
  InfoHint,
  PageHeader,
  SwitchField,
} from '@glory/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { BookOpen, FolderTree, Package, Plus, Sparkles } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import { Fragment, useMemo, useState } from 'react';

import { AiDraftDialog } from '../../../features/catalog/ai-draft-dialog.tsx';
import { CategoriesDialog, NewItemDialog } from '../../../features/catalog/dialogs.tsx';
import { ItemCard } from '../../../features/catalog/item-card.tsx';
import { orpc } from '../../../lib/orpc.ts';
import { requirePermissionRoute, usePermissions } from '../../../lib/permissions.ts';

/** Открытая услуга и фильтр живут в адресе: ссылку можно переслать, ⌘K открывает её же. */
interface CatalogSearch {
  readonly item?: string | undefined;
  readonly category?: string | undefined;
  /** Открыть «Новую услугу» — команда ⌘K. */
  readonly create?: boolean | undefined;
  /** Открыть готовый черновик ИИ — ссылка из уведомления «Черновик услуги готов». */
  readonly draft?: boolean | undefined;
}

export const Route = createFileRoute('/_app/catalog/')({
  validateSearch: (search: Record<string, unknown>): CatalogSearch => ({
    item: typeof search.item === 'string' ? search.item : undefined,
    category: typeof search.category === 'string' ? search.category : undefined,
    create: search.create === true || search.create === 'true' ? true : undefined,
    draft: search.draft === true || search.draft === 'true' ? true : undefined,
  }),
  beforeLoad: requirePermissionRoute('catalog.read'),
  component: CatalogPage,
});

const col = dataColumns<CatalogItemDto>();

function catalogColumns({
  categoryName,
  withMargin,
}: {
  categoryName: (id: string) => string;
  withMargin: boolean;
}) {
  return col.columns([
    col.accessor('name', {
      header: t('catalog.columnItem'),
      meta: { width: 'minmax(0,2fr)' },
      cell: (c) => {
        const item = c.row.original;
        return (
          <span className="flex min-w-0 flex-col">
            <span className={cn('truncate font-semibold', item.archived && 'text-muted')}>{item.name}</span>
            {item.summary ? <span className="truncate text-caption text-muted">{item.summary}</span> : null}
          </span>
        );
      },
    }),
    col.display({
      id: 'category',
      header: t('catalog.columnCategory'),
      meta: { width: 'minmax(0,1fr)', hideOnMobile: true },
      cell: (c) => <Badge>{categoryName(c.row.original.categoryId)}</Badge>,
    }),
    col.accessor('minutes', {
      header: t('catalog.columnHours'),
      meta: { width: '4.5rem', align: 'right', hideOnMobile: true },
      cell: (c) => (c.getValue() === 0 ? '—' : formatHours(c.getValue())),
    }),
    col.accessor('price', {
      header: t('catalog.columnPrice'),
      meta: { width: '7.5rem', align: 'right', cellClassName: 'font-display text-small font-bold' },
      sortUndefined: 'last',
      cell: (c) => {
        const value = c.getValue();
        return value === null ? (
          <span className="font-sans text-caption font-semibold text-muted">{t('catalog.noPrice')}</span>
        ) : (
          formatMoney(value)
        );
      },
    }),
    ...(withMargin
      ? [
          col.display({
            id: 'margin',
            header: t('catalog.columnMargin'),
            meta: { width: '5rem', align: 'right' },
            cell: (c) => {
              const costing = c.row.original.costing;
              const rate = costing?.marginRate ?? null;
              if (rate === null) return <span className="text-muted">—</span>;
              // Маржа в норме — просто число; цвет только у отклонения.
              return costing?.belowMin ? (
                <Badge tone="warn">{formatPercent(rate)}</Badge>
              ) : (
                <span className="font-semibold tabular-nums">{formatPercent(rate)}</span>
              );
            },
          }),
        ]
      : []),
  ]);
}

type OpenDialog = { readonly kind: 'new' } | { readonly kind: 'categories' } | { readonly kind: 'quick' };

function CatalogPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { can } = usePermissions();
  const canManage = can('catalog.manage');
  const seesCost = can('pricing.read');
  const [showArchived, setShowArchived] = useState(false);
  const [chosenDialog, setDialog] = useState<OpenDialog | null>(null);
  const dialog: OpenDialog | null =
    chosenDialog ??
    (search.create && canManage ? { kind: 'new' } : search.draft && canManage ? { kind: 'quick' } : null);

  const list = useQuery(orpc.catalog.items.list.queryOptions({ input: { includeArchived: showArchived } }));
  const categories = useQuery(orpc.catalog.categories.list.queryOptions());
  const specializations = useQuery(
    orpc.org.specializations.list.queryOptions({ input: { includeArchived: true } }),
  );
  const pricing = useQuery({ ...orpc.catalog.pricingContext.queryOptions(), enabled: seesCost });

  const allCategories = useMemo(() => categories.data ?? [], [categories.data]);
  const categoryName = useMemo(() => {
    const names = new Map<string, string>(allCategories.map((c) => [c.id, c.name]));
    return (id: string) => names.get(id) ?? '—';
  }, [allCategories]);
  const columns = useMemo(
    () => catalogColumns({ categoryName, withMargin: seesCost }),
    [categoryName, seesCost],
  );

  const all = list.data?.items ?? [];
  const inCategory = search.category ? all.filter((i) => i.categoryId === search.category) : all;
  // Архивную из ⌘K или по ссылке показываем, даже если архив скрыт.
  const items = inCategory.filter((i) => !i.archived || showArchived || i.id === search.item);
  const openIndex = items.findIndex((i) => i.id === search.item);
  const open = openIndex >= 0 ? items[openIndex] : undefined;

  /** Открыть карточку — новая запись истории (назад закроет её); листать и закрыть — без записи. */
  const show = (id: string | undefined, replace: boolean) => {
    void navigate({ search: (prev) => ({ ...prev, item: id }), replace });
  };
  const filter = (category: string | undefined) => {
    void navigate({ search: (prev) => ({ ...prev, category }), replace: true });
  };

  return (
    <div className="relative flex min-h-full w-full flex-col gap-4 px-4 py-5 md:px-6">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            {t('catalog.title')}
            <InfoHint hint={{ text: t('catalog.hint'), example: t('catalog.hintExample') }} />
          </span>
        }
        actions={
          <>
            {/* «Быстрый расчёт»: описание своими словами → ИИ составляет услугу → «Создать услугу». */}
            {canManage ? (
              <Button
                onClick={() => {
                  setDialog({ kind: 'quick' });
                }}
              >
                <Sparkles /> {t('catalog.aiDraft.open')}
              </Button>
            ) : null}
            <Button asChild>
              <Link to="/catalog/cases">
                <BookOpen /> {t('catalog.cases.tab')}
              </Link>
            </Button>
            {canManage ? (
              <>
                <Button
                  onClick={() => {
                    setDialog({ kind: 'categories' });
                  }}
                >
                  <FolderTree /> {t('catalog.categoriesButton')}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    setDialog(allCategories.length === 0 ? { kind: 'categories' } : { kind: 'new' });
                  }}
                >
                  <Plus /> {t('catalog.newItem')}
                </Button>
              </>
            ) : null}
          </>
        }
      />
      <p className="-mt-2 max-w-[70ch] text-muted">
        {canManage ? t('catalog.lead') : t('catalog.leadMember')}
      </p>

      {/* Фильтры над списком: категории и «Показать архив» — рядом, а не под таблицей (карта, 8). */}
      {allCategories.length > 1 || canManage ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {allCategories.length > 1 ? (
            <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('catalog.columnCategory')}>
              {[{ id: undefined, name: t('audit.anyone') }, ...allCategories].map((category) => {
                const active = search.category === category.id;
                return (
                  <button
                    key={category.id ?? 'all'}
                    type="button"
                    aria-pressed={active}
                    onClick={() => {
                      filter(category.id);
                    }}
                    className={cn(
                      'rounded-full border-thick px-2.5 py-0.5 text-small font-semibold transition-colors',
                      active
                        ? 'border-strong bg-strong text-on-strong'
                        : 'border-border text-muted hover:text-text',
                    )}
                  >
                    {category.name}
                  </button>
                );
              })}
            </div>
          ) : null}
          {canManage ? (
            <SwitchField
              inline
              className="ml-auto"
              label={t('catalog.showArchived')}
              checked={showArchived}
              onCheckedChange={setShowArchived}
            />
          ) : null}
        </div>
      ) : null}

      {list.isError ? (
        <Callout>{list.error.message}</Callout>
      ) : (
        <DataTable
          aria-label={t('catalog.title')}
          data={items}
          columns={columns}
          getRowId={(item) => item.id}
          loading={list.isPending}
          activeRowId={open?.id ?? null}
          onRowActivate={(item) => {
            show(item.id, open !== undefined);
          }}
          empty={
            <EmptyState
              icon={<Package />}
              title={t('catalog.emptyTitle')}
              description={canManage ? t('catalog.emptyText') : t('catalog.emptyMember')}
              action={
                canManage ? (
                  <Button
                    variant="primary"
                    onClick={() => {
                      setDialog(allCategories.length === 0 ? { kind: 'categories' } : { kind: 'new' });
                    }}
                  >
                    <Plus /> {t('catalog.newItem')}
                  </Button>
                ) : null
              }
            />
          }
        />
      )}

      <AnimatePresence initial={false}>
        {open ? (
          <Fragment key="panel">
            <ItemCard
              key={open.id}
              item={open}
              categories={allCategories}
              specializations={specializations.data?.items ?? []}
              pricing={pricing.data ?? null}
              canManage={canManage}
              onDuplicated={(id) => {
                show(id, true);
              }}
            >
              {(card) => (
                <HybridPanel
                  title={card.title}
                  meta={card.meta}
                  tabs={card.tabs}
                  actions={card.actions}
                  onClose={() => {
                    show(undefined, true);
                  }}
                  onPrevious={() => {
                    const previous = items[openIndex - 1];
                    if (previous) show(previous.id, true);
                  }}
                  onNext={() => {
                    const next = items[openIndex + 1];
                    if (next) show(next.id, true);
                  }}
                  onExpand={() => {
                    void navigate({ to: '/catalog/$id', params: { id: open.id } });
                  }}
                >
                  {card.body}
                </HybridPanel>
              )}
            </ItemCard>
          </Fragment>
        ) : null}
      </AnimatePresence>

      <Dialog
        open={dialog !== null}
        onOpenChange={(next) => {
          if (next) return;
          setDialog(null);
          if (search.create || search.draft)
            void navigate({
              search: (prev) => ({ ...prev, create: undefined, draft: undefined }),
              replace: true,
            });
        }}
      >
        {dialog?.kind === 'new' ? (
          <NewItemDialog
            categories={allCategories}
            initialCategoryId={search.category ?? null}
            onDone={(id) => {
              setDialog(null);
              void navigate({ search: (prev) => ({ ...prev, create: undefined, item: id }) });
            }}
          />
        ) : null}
        {dialog?.kind === 'categories' ? <CategoriesDialog categories={allCategories} /> : null}
        {dialog?.kind === 'quick' ? (
          <AiDraftDialog
            categories={allCategories}
            specializations={(specializations.data?.items ?? []).filter((s) => !s.archived)}
            initialCategoryId={search.category ?? null}
            resume={search.draft === true}
            onDone={(id) => {
              setDialog(null);
              void navigate({ search: (prev) => ({ ...prev, draft: undefined, item: id }) });
            }}
          />
        ) : null}
      </Dialog>
    </div>
  );
}
